import { useState, useEffect } from "react"

interface FetchOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE"
  headers?: HeadersInit
  body?: string
}

interface PaginationVars {
  count: number
  count_args: string[]
  ends: boolean
  items: string
  limit: number
  outset: number
  page: number
  page_param: string
  size: number
}

interface PaginationMetadata {
  count: number
  from: number
  in: number
  last: number
  limit: number
  next: number | null
  offset: number
  outset: number
  page: number
  prev: number | null
  to: number
  vars: PaginationVars
}

interface FetchState<T> {
  data: T | null
  isLoading: boolean
  isRefetching: boolean
  error: Error | null
  metadata?: PaginationMetadata
}

interface UseFetchResource<T, K extends keyof T | undefined = undefined> {
  data: K extends keyof T ? T[K] : T | null
  metadata?: PaginationMetadata
  isLoading: boolean
  isRefetching: boolean
  error: Error | null
  refetch: (params?: Record<string, string>) => Promise<void>
}

function useFetchResource<T extends object, K extends keyof T | undefined = undefined>(
  initialUrl: string,
  dataKey?: K,
  initialParams?: Record<string, string>,
  options: FetchOptions = {}
): UseFetchResource<T, K> {
  const [state, setState] = useState<FetchState<K extends keyof T ? T[K] : T>>({
    data: null,
    isLoading: true,
    isRefetching: true,
    error: null,
    metadata: undefined,
  })

  const buildUrl = (baseUrl: string, params?: Record<string, string>) => {
    if (!params) return baseUrl
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value)
      }
    })
    const queryString = searchParams.toString()
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
  }

  const fetchData = async (params?: Record<string, string>) => {
    // TODO: change isRefetching to isLoading
    setState((prev) => ({ ...prev, isRefetching: true, error: null }))

    try {
      const url = buildUrl(initialUrl, params)
      const response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData: T = await response.json()

      // Extract metadata if it exists
      const metadata = "metadata" in responseData ? (responseData as any).metadata : undefined

      // Extract the specific data key if provided, otherwise use the whole response
      const extractedData = dataKey ? responseData[dataKey] : responseData

      setState({
        data: extractedData as K extends keyof T ? T[K] : T,
        isLoading: false,
        isRefetching: false,
        error: null,
        metadata,
      })
    } catch (error) {
      setState({
        data: null,
        isLoading: false,
        isRefetching: false,
        error: error instanceof Error ? error : new Error("An error occurred"),
        metadata: undefined,
      })
    }
  }

  useEffect(() => {
    fetchData(initialParams)
  }, [initialUrl]) // Only re-run if the base URL changes

  return {
    data: state.data || (initialUrl.endsWith("s") ? [] : ({} as any)),
    metadata: state.metadata,
    isLoading: state.isLoading,
    isRefetching: state.isRefetching,
    error: state.error,
    refetch: fetchData,
  }
}

export default useFetchResource
